'use client';

import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
    onCyberArmyTurnstileLoad?: () => void;
  }
}

type Props = {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  theme?: 'dark' | 'light' | 'auto';
  appearance?: 'always' | 'execute' | 'interaction-only';
  resetKey?: number;
  className?: string;
};

export default function Turnstile({
  siteKey,
  onVerify,
  onExpire,
  onError,
  theme = 'dark',
  appearance = 'interaction-only',
  resetKey = 0,
  className,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  // Keep the latest callbacks without re-rendering the widget.
  const cbs = useRef({ onVerify, onExpire, onError });
  cbs.current = { onVerify, onExpire, onError };

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;

    const render = () => {
      if (cancelled || !ref.current || !window.turnstile || widgetId.current !== null) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        theme,
        appearance,
        callback: (t: string) => cbs.current.onVerify(t),
        'expired-callback': () => cbs.current.onExpire && cbs.current.onExpire(),
        'error-callback': () => cbs.current.onError && cbs.current.onError(),
      });
    };

    window.onCyberArmyTurnstileLoad = render;
    const scriptId = 'cf-turnstile-script';
    if (document.getElementById(scriptId)) {
      if (window.turnstile) render();
    } else {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onCyberArmyTurnstileLoad';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      try {
        if (window.turnstile && widgetId.current) window.turnstile.remove(widgetId.current);
      } catch {
        /* ignore */
      }
      widgetId.current = null;
    };
  }, [siteKey, theme, appearance]);

  // Tokens are single-use; bumping resetKey fetches a fresh one after a failed submit.
  useEffect(() => {
    if (resetKey > 0 && window.turnstile && widgetId.current) {
      try {
        window.turnstile.reset(widgetId.current);
      } catch {
        /* ignore */
      }
    }
  }, [resetKey]);

  if (!siteKey) return null;
  return <div ref={ref} className={className} />;
}
